import {gql} from '@apollo/client';
import React from 'react';

import {ActionOrigin, ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ItemListingQuery} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {getExecuteMessageForTasksWithSchema} from 'processes/dashboard/ExecuteTasksButton';
import {ProcessWithStatusCounts} from 'processes/dashboard/ProcessTaskStatusCounts';

import {ProcessOutputGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputGraphQL';
import {ProcessTaskGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';
import {
  ProcessManagementDashboardProcessToTasksQuery,
  ProcessManagementDashboardProcessToTasksQueryVariables,
} from './__generated/ProcessManagementDashboardProcessToTasksQuery';
import ExecuteButton from './ExecuteButton';

type Process = ExtractByTypename<
  ProcessManagementDashboardProcessToTasksQuery['processes'][number],
  'ProcessNode'
>;

type ProcessTaskSchema = ExtractByTypename<
  ExtractByTypename<ItemListingQuery['processes'][number], 'ProcessNode'>['processTaskSchema'],
  'ProcessTaskSchemaNode'
>;

type ProcessTask = Process['tasks'][number];

export const FETCH_PROCESS_TASKS_FOR_PROCESSES_QUERY = gql`
  query ProcessManagementDashboardProcessToTasksQuery($processIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processes(ids: $processIds) {
      ... on ProcessNode {
        id
        tasks {
          ...ProcessTaskListingGraphQL
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcessTaskListing').fragment}
`;

export async function getProcessTasksForProcesses(processIds: Array<string>): Promise<Array<ProcessTask>> {
  const {processes} = await doQuerySafe<
    ProcessManagementDashboardProcessToTasksQuery,
    ProcessManagementDashboardProcessToTasksQueryVariables
  >({query: FETCH_PROCESS_TASKS_FOR_PROCESSES_QUERY}, {variables: {processIds}, fetchPolicy: 'network-only'});
  const processTasks = processes
    .filter((proc) => !isInaccessible(proc))
    .map((proc: Process) => proc.tasks)
    .flat();
  const executableTasks = processTasks.filter(
    (task) => task.status.statusType === ProcessTaskStatusType.PENDING
  );

  return executableTasks;
}

function getNumPendingTasks(process: ProcessForExecuteTaskGroupsButton): number {
  return process.taskStatusCounts.reduce(
    (total, {count, status}) => (status.statusType === ProcessTaskStatusType.PENDING ? total + count : total),
    0
  );
}

function getExecuteTaskGroupsTooltipMessage(
  processes: Array<ProcessForExecuteTaskGroupsButton>,
  isSidePanel: boolean = false
): string | null {
  const numPendingTasks = processes.reduce((total, proc) => total + getNumPendingTasks(proc), 0);
  if (processes.length === 0) {
    return 'No task groups selected';
  } else if (processes.some((proc) => proc.processTaskSchema.id !== processes[0].processTaskSchema.id)) {
    return 'All task groups must have the same task schema';
  } else if (numPendingTasks === 0) {
    return `${
      processes.length === 1 ? 'Task group does' : 'Selected task groups do'
    } not have any pending tasks`;
  }

  return getExecuteMessageForTasksWithSchema(processes[0].processTaskSchema, numPendingTasks, isSidePanel);
}

export interface ProcessForExecuteTaskGroupsButton extends ProcessWithStatusCounts {
  id: string;
  processTaskSchema: ProcessTaskSchema;
}

interface ExecuteTaskGroupsProps {
  processes: Array<ProcessForExecuteTaskGroupsButton>;
  onTasksOrOutputsChange?: (tasks: Array<ProcessTaskGraphQL>, outputs?: Array<ProcessOutputGraphQL>) => void;
  // If true, asserts one and only one process is provided
  actionOrigin?: ActionOrigin;
  isSidePanel?: boolean;
}

export default function ExecuteTaskGroupsButton({
  processes,
  onTasksOrOutputsChange,
  actionOrigin,
  isSidePanel,
}: ExecuteTaskGroupsProps): JSX.Element {
  const processIds = processes.map((proc) => proc.id);

  const tooltipTitle = getExecuteTaskGroupsTooltipMessage(processes, isSidePanel ?? false);
  return (
    <ExecuteButton
      getTasksToExecute={async () => await getProcessTasksForProcesses(processIds)}
      disabledMessage={tooltipTitle}
      onTasksOrOutputsChange={onTasksOrOutputsChange}
      actionOrigin={actionOrigin}
    />
  );
}
